import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { Waveform } from "@/components/site/Waveform";
import { WebChatWidget } from "@/components/site/WebChatWidget";
import { CallWidget } from "@/components/site/CallWidget";
import { LiveTranscript } from "@/components/site/LiveTranscript";
import { PbxVisual } from "@/components/site/PbxVisuals";
import { FeatureDemo } from "@/components/site/FeatureDemos";
import { ReportingDashboard } from "@/components/site/AnalyticsDashboard";
import { pages } from "@/data/pages";

const waveformScript = [
  { who: "AI", text: "How can I help you today?" },
  { who: "Caller", text: "Mujhe demo book karni hai." },
  { who: "AI", text: "Sure — I can set that up right now." },
  { who: "Caller", text: "Great, please go ahead." },
  { who: "AI", text: "Done! You'll get a confirmation shortly." },
];

const HeroVisual = ({ data }) => {
  if (data.visual === "waveform") {
    return (
      <div className="bg-[#0a0a0a] text-white border border-black p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <span className="font-mono-t text-[11px] uppercase tracking-widest text-neutral-300 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live · AI agent
          </span>
          <span className="font-mono-t text-[11px] text-neutral-500">EN · HI · Hinglish</span>
        </div>
        <div className="py-8"><Waveform bars={48} height={70} color="#ffffff" /></div>
        <div className="border-t border-white/10 pt-4">
          <LiveTranscript lines={waveformScript} className="min-h-[132px]" />
        </div>
      </div>
    );
  }
  if (data.visual === "chat") {
    return <WebChatWidget />;
  }
  if (data.visual === "call") {
    return <CallWidget />;
  }
  if (data.visual === "pbx") {
    return <PbxVisual kind={data.pbxKind} />;
  }
  return (
    <div className="bg-[#0a0a0a] text-white border border-black p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]">
      <span className="font-mono-t text-[11px] uppercase tracking-widest text-neutral-400">Included</span>
      <ul className="mt-5 space-y-3.5">
        {data.bullets.slice(0, 6).map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm">
            <Check size={16} className="text-white mt-0.5 shrink-0" /> {b}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function SubPage({ slug }) {
  const data = pages[slug];
  if (!data) return null;

  return (
    <div data-testid={`subpage-${slug.replace("/", "-")}`}>
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }} />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-5">{data.category}</p>
            </Reveal>
            <h1 className="font-heading font-medium tracking-tight leading-[0.98] text-4xl md:text-5xl lg:text-6xl text-white">
              <MaskedLines lines={[data.title]} delay={0.1} />
            </h1>
            <Reveal delay={0.3}>
              <p className="mt-6 text-base md:text-lg text-neutral-400 leading-relaxed max-w-lg">{data.lead}</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/demo" className="group inline-flex items-center gap-2 rounded-lg bg-neutral-100 text-neutral-900 font-bold px-6 py-3.5 hover:bg-white transition-colors duration-300" data-testid="subpage-cta-demo">
                  Book a demo <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center gap-2 rounded-lg border border-white/20 text-white font-semibold px-6 py-3.5 hover:border-white/50 hover:bg-white/5 transition-colors duration-300">
                  View pricing
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}><HeroVisual data={data} /></Reveal>
        </div>
      </section>

      {/* Languages strip */}
      {data.languages && (
        <section className="py-16 border-t border-white/10" data-testid="languages-strip">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Languages</p>
              <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white max-w-xl">
                Covers 10+ Indian languages.
              </h2>
            </Reveal>
            <div className="mt-8 flex flex-wrap gap-3">
              {data.languages.map((l, i) => (
                <Reveal key={l} delay={(i % 8) * 0.03}>
                  <span className="inline-block border border-white/15 px-4 py-2 text-sm font-medium text-neutral-200 hover:bg-white hover:text-neutral-900 hover:border-white transition-colors duration-300">
                    {l}
                  </span>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Chat-to-call escalation section (web-chat page addition) — shown before "What you get" */}
      {data.showCallWidget && (
        <section className="py-20 border-t border-white/10" data-testid="call-escalation-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">From chat to call</p>
              <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-white max-w-lg">
                When a chat needs a voice, it picks up the phone.
              </h2>
              <p className="mt-5 text-neutral-400 leading-relaxed max-w-md">
                The same agent escalates a website chat into a live call — audio flowing and transcription streaming
                in real time, right on your site. One brain, both channels.
              </p>
            </Reveal>
            <Reveal delay={0.15}><CallWidget /></Reveal>
          </div>
        </section>
      )}

      {/* Cards — detailed animated rows for PBX pages, simple grid otherwise */}
      {data.visual === "pbx" ? (
        <section className="py-20 border-t border-white/10" data-testid="pbx-feature-detail">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white max-w-xl">What you get</h2>
              <p className="mt-3 text-neutral-500 max-w-md">Every capability, explained — and shown live.</p>
            </Reveal>
            <div className="mt-14 border-y border-white/10 divide-y divide-white/10">
              {data.cards.map((c, i) => (
                <div key={c.title} className="py-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                  <motion.div
                    className={i % 2 === 1 ? "lg:order-2" : ""}
                    initial={{ opacity: 0, x: i % 2 === 1 ? 40 : -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="flex items-center gap-3">
                      <motion.span
                        className="grid place-items-center w-11 h-11 bg-neutral-900 text-white"
                        initial={{ scale: 0, rotate: -20 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
                      >
                        <c.icon size={20} />
                      </motion.span>
                      <span className="font-mono-t text-xs uppercase tracking-widest text-[#0047FF]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 font-heading font-medium text-2xl md:text-3xl tracking-tight text-white inline-block">
                      {c.title}
                      <motion.span
                        className="block h-[2px] bg-[#0047FF] mt-2 origin-left"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </h3>
                    <p className="mt-4 text-neutral-400 leading-relaxed max-w-md">{c.detail || c.desc}</p>
                  </motion.div>
                  <motion.div
                    className={i % 2 === 1 ? "lg:order-1" : ""}
                    initial={{ opacity: 0, x: i % 2 === 1 ? -40 : 40, scale: 0.96 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                  >
                    <FeatureDemo kind={c.anim} />
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white max-w-xl">What you get</h2>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {data.cards.map((c, i) => (
                <Reveal key={c.title} delay={(i % 3) * 0.06}>
                  <div className="h-full bg-white/[0.02] p-7 hover:bg-white/[0.05] transition-colors duration-300 group min-h-[190px]">
                    <c.icon className="text-white group-hover:scale-110 transition-transform origin-left" size={24} />
                    <h3 className="mt-5 font-heading font-semibold text-lg text-white">{c.title}</h3>
                    <p className="mt-2 text-neutral-500 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reporting / analytics dashboard */}
      {data.reporting && (
        <section className="py-20 border-t border-white/10 bg-white/[0.02]" data-testid="reporting-section">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">
                {data.reporting === "pbx" ? "PBX reporting" : "AI analytics"}
              </p>
              <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl lg:text-4xl text-white max-w-2xl">
                {data.reporting === "pbx"
                  ? "See your whole phone system at a glance."
                  : "Every conversation, measured automatically."}
              </h2>
              <p className="mt-4 text-neutral-400 max-w-xl">
                {data.reporting === "pbx"
                  ? "Live call volume, answer rates, queue wait times and trunk usage across every tenant — updated in real time."
                  : "Sentiment, intent, topics and outcomes scored on every call, rolled up into dashboards you can act on."}
              </p>
            </Reveal>
            <div className="mt-12">
              <ReportingDashboard kind={data.reporting} />
            </div>
          </div>
        </section>
      )}

      {/* Bullets */}
      <section className="py-20 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">Everything included</p>
            <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white max-w-xl">Feature checklist</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {data.bullets.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="flex items-start gap-3 py-3 border-b border-white/10 group"
              >
                <motion.span
                  className="grid place-items-center w-5 h-5 mt-0.5 shrink-0 bg-neutral-900 group-hover:bg-[#0047FF] transition-colors"
                  initial={{ scale: 0, rotate: -30 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 320, damping: 16, delay: (i % 3) * 0.06 + 0.1 }}
                >
                  <Check size={13} className="text-white" />
                </motion.span>
                <span className="text-sm text-neutral-300">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-24 bg-[#0a0a0a] text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl max-w-xl">See it handle a real call.</h2>
          <Link to="/demo" className="group inline-flex items-center gap-2 bg-white text-black font-bold px-7 py-4 hover:bg-neutral-200 transition-colors duration-300 shrink-0" data-testid="subpage-cta-band">
            Book a demo <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
